import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingCart, Heart } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

export default function ProductCard({ product, onAddToCart, onToggleWishlist, isInWishlist, index = 0, size = "normal" }) {
  const { lang, t } = useLanguage();
  const name = lang === "de" ? product.name_de : product.name_en;
  const image = product.images?.[0] || "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=800&q=80";

  return (
    <motion.div
      data-testid={`product-card-${product.id}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.08, ease: [0.25, 0.1, 0.25, 1] }}
      className="group relative overflow-hidden rounded-sm bg-surface border border-border hover:border-foreground/30 transition-all duration-500 cursor-pointer"
      style={{
        boxShadow: "0 0 0 0 rgba(255,255,255,0)",
      }}
      whileHover={{ boxShadow: "0 0 30px 0 rgba(255,255,255,0.05), 0 20px 40px -15px rgba(0,0,0,0.3)" }}
    >
      <Link to={`/shop/${product.id}`} className="block">
        {/* Image */}
        <div className={`relative overflow-hidden ${size === "large" ? "aspect-[4/3]" : "aspect-square"} bg-background`}>
          <img
            src={image}
            alt={name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

          {/* Status Badge */}
          {product.status === "featured" && (
            <span className="absolute top-3 left-3 px-2 py-1 bg-foreground text-background text-[10px] font-mono tracking-widest uppercase rounded-sm">
              {t("product.featured")}
            </span>
          )}

          {/* Quick Actions */}
          <div className="absolute bottom-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
            {onToggleWishlist && (
              <button
                data-testid={`wishlist-btn-${product.id}`}
                onClick={(e) => { e.preventDefault(); onToggleWishlist(product); }}
                className={`p-2 rounded-sm backdrop-blur-sm transition-all duration-200 ${
                  isInWishlist
                    ? "bg-foreground text-background"
                    : "bg-background/70 text-foreground hover:bg-foreground hover:text-background"
                }`}
              >
                <Heart size={14} strokeWidth={1.5} fill={isInWishlist ? "currentColor" : "none"} />
              </button>
            )}
            {onAddToCart && (
              <button
                data-testid={`add-to-cart-${product.id}`}
                onClick={(e) => { e.preventDefault(); onAddToCart(product); }}
                className="p-2 rounded-sm bg-foreground text-background backdrop-blur-sm hover:bg-foreground/90 transition-all duration-200"
              >
                <ShoppingCart size={14} strokeWidth={1.5} />
              </button>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-mono tracking-widest uppercase text-foreground/40 mb-1">{product.category}</p>
              <h3 className="text-sm font-heading font-medium text-foreground truncate group-hover:text-foreground transition-colors">
                {name}
              </h3>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-sm font-mono font-medium text-foreground">€{product.price?.toFixed(2)}</p>
              {product.stock <= 0 && (
                <p className="text-[10px] text-destructive font-mono">{t("product.out_of_stock")}</p>
              )}
            </div>
          </div>
          {product.material && (
            <p className="text-[11px] text-foreground/40 font-mono mt-1.5 truncate">{product.material}</p>
          )}
        </div>
      </Link>
    </motion.div>
  );
}
